import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useColorContext } from '../../../context/colorContext';
import { FullscreenToggleButton } from '../../ui/buttons/Button';
import SmartFlashCardElement from './smartFlashCardElements';

const flattenFlashcards = (payload = {}) => {
  if (!payload || typeof payload !== 'object') return [];
  if (Array.isArray(payload.flashcards)) {
    return payload.flashcards;
  }
  if (Array.isArray(payload.parts)) {
    return payload.parts.flatMap((part) => Array.isArray(part.flashcards) ? part.flashcards : []);
  }
  return [];
};

const SmartFlashCardFeature = ({
  result,
  onFinish,
  onWrong,
  onRight,
  onIndexChange,
  style = {},
  className = '',
  emptyMessage = 'No flashcards available yet.',
  progressTitle = 'Progression',
  showProgress = true,
  labels = {},
  icons = {},
  layout = {},
  buttonMotion = {},
  styles = {},
  flashCardProps = {},
  loopOnFinish = true,
  startIndex = 0,
  resetIndexOnResultChange = true,
}) => {
  const { textPrimary, textSecondary, bgSecondary, activeAccent } = useColorContext();
  const rootRef = useRef(null);
  const flashcards = useMemo(() => flattenFlashcards(result), [result]);
  const sectionTitle = useMemo(() => {
    if (!result || typeof result !== 'object') return '';
    return String(
      result.title
      || result.metadata?.sectionTitle
      || result.parts?.[0]?.title
      || ''
    ).trim();
  }, [result]);
  const total = flashcards.length;
  const [isFullscreen, setIsFullscreen] = useState(false);

  const resolvedLabels = {
    wrong: 'Got it wrong',
    right: 'Got it right',
    ...labels,
  };

  const resolvedIcons = {
    wrong: '✕',
    right: '✓',
    ...icons,
  };

  const resolvedLayout = {
    maxWidth: '760px',
    minHeight: '62vh',
    cardMinHeight: '40vh',
    buttonMinWidth: '165px',
    actionsGap: '1rem',
    progressGap: '1rem',
    progressBarHeight: '6px',
    progressMarginTop: '0.75rem',
    progressSectionMarginBottom: '1rem',
    cardSectionMarginBottom: '1.25rem',
    ...layout,
  };

  const resolvedMotion = {
    hoverTranslateY: '-2px',
    activeScale: 0.99,
    hoverBrightness: 1.03,
    transition: 'transform 180ms cubic-bezier(0.22, 1, 0.36, 1), box-shadow 180ms ease, filter 180ms ease',
    ...buttonMotion,
  };

  const initialIndex = useMemo(() => {
    if (total <= 0) return 0;
    const normalized = Number.isFinite(startIndex) ? Math.floor(startIndex) : 0;
    return Math.min(Math.max(normalized, 0), total - 1);
  }, [total, startIndex]);

  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [correctCount, setCorrectCount] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);

  useEffect(() => {
    if (!resetIndexOnResultChange) return;
    setCurrentIndex(initialIndex);
    setCorrectCount(0);
    setIsCompleted(false);
  }, [result, initialIndex, resetIndexOnResultChange]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(document.fullscreenElement === rootRef.current);
    };

    if (typeof document !== 'undefined') {
      document.addEventListener('fullscreenchange', handleFullscreenChange);
    }

    return () => {
      if (typeof document !== 'undefined') {
        document.removeEventListener('fullscreenchange', handleFullscreenChange);
      }
    };
  }, []);

  const currentFlashcard = flashcards[currentIndex] || null;
  const scoreOn20 = total > 0 ? Math.round((correctCount / total) * 20) : 0;

  const resetSession = () => {
    setCurrentIndex(initialIndex);
    setCorrectCount(0);
    setIsCompleted(false);
    onIndexChange?.(initialIndex, flashcards[initialIndex] || null, { origin: 'restart', restarted: true });
  };

  const handleAdvance = (origin) => {
    setCurrentIndex((prev) => {
      if (total <= 0) {
        return 0;
      }
      if (prev < total - 1) {
        const next = prev + 1;
        onIndexChange?.(next, flashcards[next], { origin });
        return next;
      }
      if (typeof onFinish === 'function') {
        onFinish({ origin, currentIndex: prev, flashcard: flashcards[prev] });
      }
      setIsCompleted(true);
      onIndexChange?.(prev, flashcards[prev], { origin, finished: true });
      return loopOnFinish ? 0 : prev;
    });
  };

  const handleGotItWrong = () => {
    if (isCompleted) {
      resetSession();
      return;
    }
    onWrong?.({ currentIndex, flashcard: currentFlashcard });
    handleAdvance('wrong');
  };

  const handleGotItRight = () => {
    if (isCompleted) {
      resetSession();
      return;
    }
    onRight?.({ currentIndex, flashcard: currentFlashcard });
    setCorrectCount((value) => value + 1);
    handleAdvance('right');
  };

  const toggleFullscreen = async () => {
    if (!rootRef.current || typeof document === 'undefined') return;

    try {
      if (document.fullscreenElement === rootRef.current) {
        await document.exitFullscreen();
        return;
      }
      await rootRef.current.requestFullscreen();
    } catch (error) {
      // Ignore API errors to keep UI interaction smooth.
    }
  };

  const createButtonStyle = (kind) => {
    const isWrong = kind === 'wrong';
    return {
      flex: '1 1 140px',
      minWidth: resolvedLayout.buttonMinWidth,
      padding: '0.9rem 1.15rem',
      borderRadius: '1rem',
      border: isWrong ? `1px solid ${activeAccent}2b` : '1px solid transparent',
      background: isWrong
        ? `linear-gradient(135deg, ${activeAccent}12 0%, ${activeAccent}1b 100%)`
        : `linear-gradient(135deg, ${activeAccent}d9 0%, ${activeAccent}ff 100%)`,
      color: isWrong ? textPrimary : '#ffffff',
      fontWeight: 600,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.55rem',
      cursor: total === 0 ? 'not-allowed' : 'pointer',
      opacity: total === 0 ? 0.45 : 1,
      boxShadow: isWrong
        ? `0 8px 22px ${activeAccent}1f, inset 0 1px 0 rgba(255,255,255,0.06)`
        : `0 10px 28px ${activeAccent}33`,
      transform: 'translateY(0)',
      transition: resolvedMotion.transition,
      ...(isWrong ? styles.wrongButton : styles.rightButton),
    };
  };

  const restartButtonStyle = {
    padding: '0.55rem 0.98rem',
    borderRadius: '999px',
    border: `1px solid ${activeAccent}28`,
    background: `${activeAccent}10`,
    color: textPrimary,
    fontSize: '0.85rem',
    fontWeight: 700,
    letterSpacing: '0.02em',
    cursor: total === 0 ? 'not-allowed' : 'pointer',
    opacity: total === 0 ? 0.5 : 1,
    boxShadow: `0 8px 20px ${activeAccent}14`,
    transition: resolvedMotion.transition,
  };

  const handleButtonEnter = (event, kind) => {
    if (total === 0) return;
    const isWrong = kind === 'wrong';
    event.currentTarget.style.transform = `translateY(${resolvedMotion.hoverTranslateY})`;
    event.currentTarget.style.boxShadow = isWrong
      ? `0 12px 28px ${activeAccent}2b, inset 0 1px 0 rgba(255,255,255,0.08)`
      : `0 14px 34px ${activeAccent}40`;
    event.currentTarget.style.filter = `brightness(${resolvedMotion.hoverBrightness})`;
  };

  const handleButtonLeave = (event, kind) => {
    const isWrong = kind === 'wrong';
    event.currentTarget.style.transform = 'translateY(0)';
    event.currentTarget.style.boxShadow = isWrong
      ? `0 8px 22px ${activeAccent}1f, inset 0 1px 0 rgba(255,255,255,0.06)`
      : `0 10px 28px ${activeAccent}33`;
    event.currentTarget.style.filter = 'brightness(1)';
  };

  const handleButtonDown = (event) => {
    if (total === 0) return;
    event.currentTarget.style.transform = `translateY(0) scale(${resolvedMotion.activeScale})`;
  };

  const handleButtonUp = (event) => {
    if (total === 0) return;
    event.currentTarget.style.transform = `translateY(${resolvedMotion.hoverTranslateY}) scale(1)`;
  };

  const progressPercent = total > 0 ? Math.round(((currentIndex + 1) / total) * 100) : 0;

  return (
    <div
      ref={rootRef}
      className={className}
      style={{
        width: '100%',
        maxWidth: isFullscreen ? '100%' : resolvedLayout.maxWidth,
        margin: '0 auto',
        minHeight: isFullscreen ? '100vh' : resolvedLayout.minHeight,
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        padding: isFullscreen ? '1rem' : undefined,
        background: isFullscreen ? bgSecondary : undefined,
        ...styles.container,
        ...style,
      }}
    >
      <FullscreenToggleButton
        isFullscreen={isFullscreen}
        onToggle={toggleFullscreen}
        size="md"
        color={activeAccent}
        position="top-right"
        offset={12}
        style={{ zIndex: 20, ...(styles.fullscreenButton || {}) }}
      />

      {showProgress && (
      <div style={{ marginBottom: resolvedLayout.progressSectionMarginBottom, ...styles.progressSection }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: resolvedLayout.progressGap, flexWrap: 'wrap', ...styles.progressRow }}>
          <div>
            <p style={{ margin: 0, color: textSecondary, fontSize: '0.82rem', letterSpacing: '0.08em', textTransform: 'uppercase', ...styles.progressTitle }}>
              {isCompleted ? 'Results' : sectionTitle || progressTitle}
            </p>
            <p style={{ margin: '0.35rem 0 0', color: textPrimary, fontSize: '1rem', fontWeight: 600, ...styles.progressText }}>
              {isCompleted
                ? `Final score: ${scoreOn20}/20`
                : `Flashcard ${total === 0 ? 0 : currentIndex + 1} / ${total}`}
            </p>
          </div>
          <div style={{ flex: '1 1 100%', height: resolvedLayout.progressBarHeight, borderRadius: '999px', background: `${textSecondary}1a`, overflow: 'hidden', marginTop: resolvedLayout.progressMarginTop, ...styles.progressTrack }}>
            <div style={{ width: `${isCompleted ? 100 : progressPercent}%`, height: '100%', borderRadius: '999px', background: activeAccent, transition: 'width 260ms ease', ...styles.progressFill }} />
          </div>
        </div>
      </div>
      )}

      {isCompleted ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem', borderRadius: '1.5rem', background: bgSecondary, color: textPrimary, textAlign: 'center', boxShadow: '0 16px 40px rgba(0,0,0,0.15)', ...styles.emptyState }}>
          <div style={{ width: '100%', maxWidth: '32rem' }}>
            <p style={{ margin: '0 0 0.6rem', color: textSecondary, fontSize: '0.85rem', letterSpacing: '0.08em', textTransform: 'uppercase', ...styles.emptyText }}>
              Session completed
            </p>
            <p style={{ margin: 0, fontSize: '2.5rem', fontWeight: 800, lineHeight: 1.1, ...styles.emptyText }}>
              {scoreOn20}/20
            </p>
            <p style={{ margin: '0.85rem 0 0', fontSize: '1rem', color: textSecondary, ...styles.emptyText }}>
              {correctCount} correct answer{correctCount > 1 ? 's' : ''} out of {total}.
            </p>
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1.25rem' }}>
              <button
                type="button"
                onClick={resetSession}
                disabled={total === 0}
                style={restartButtonStyle}
              >
                Restart
              </button>
            </div>
          </div>
        </div>
      ) : currentFlashcard ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: resolvedLayout.cardMinHeight, marginBottom: resolvedLayout.cardSectionMarginBottom, ...styles.cardArea }}>
          <SmartFlashCardElement
            show={currentFlashcard.show}
            hide={currentFlashcard.hide}
            style={{ width: '100%', maxWidth: '100%', ...(styles.card || {}) }}
            {...flashCardProps}
          />
        </div>
      ) : (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem', borderRadius: '1.5rem', background: bgSecondary, color: textSecondary, textAlign: 'center', boxShadow: '0 16px 40px rgba(0,0,0,0.15)', ...styles.emptyState }}>
          <p style={{ margin: 0, fontSize: '1rem', ...styles.emptyText }}>{emptyMessage}</p>
        </div>
      )}

      {!isCompleted && (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', ...styles.actionsRow }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: resolvedLayout.actionsGap, flexWrap: 'wrap', alignItems: 'center' }}>
        <button
          type="button"
          onClick={handleGotItWrong}
          onMouseEnter={(event) => handleButtonEnter(event, 'wrong')}
          onMouseLeave={(event) => handleButtonLeave(event, 'wrong')}
          onMouseDown={handleButtonDown}
          onMouseUp={handleButtonUp}
          disabled={total === 0}
          style={createButtonStyle('wrong')}
        >
          <span style={{ color: '#e26a6a', ...(styles.wrongIcon || {}) }}>{resolvedIcons.wrong}</span>
          <span>{resolvedLabels.wrong}</span>
        </button>

        <button
          type="button"
          onClick={handleGotItRight}
          onMouseEnter={(event) => handleButtonEnter(event, 'wrong')}
          onMouseLeave={(event) => handleButtonLeave(event, 'wrong')}
          onMouseDown={handleButtonDown}
          onMouseUp={handleButtonUp}
          disabled={total === 0}
          style={createButtonStyle('wrong')}
        >
          <span style={{ color: '#10B981', ...(styles.rightIcon || {}) }}>{resolvedIcons.right}</span>
          <span>{resolvedLabels.right}</span>
        </button>
        </div>
      </div>
      )}
    </div>
  );
};

export default SmartFlashCardFeature;
